import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import { FormGroup, Intent, RadioGroup, Radio } from "@blueprintjs/core";
import css from "../form.module.scss";
import { IRadioFieldProps } from "../models";
import { FastField, FieldProps, getIn } from "formik";
import RequiredIndicator from "../../../shared/RequiredIndicator";
import { validationMethodFactory, hasRequiredValidation } from "../models/validation";
import useFormContext from "../FormContext";
import { Orientation } from "../models/baseTypes";
import CheckIcon from "./PrintedCheckIcon";



type  Props<T=any> = FieldProps<T> & { fieldProps: IRadioFieldProps, className: string };


function normalizeValue(value: string | number | boolean = '') {
  //value = typeof value === "boolean" ? Number(value) : value;
  return String(value);
}

const BoundRadioGroupField = ({ field, form, fieldProps }: Props) => {
  const { isEditing, isPrinting, fieldChangedSignal, fieldPropOverrides } = useFormContext();
  const error = (form.submitCount || getIn(form.touched, field.name)) && getIn(form.errors, field.name);
  const intent = error ? Intent.DANGER : Intent.NONE;
  let value = normalizeValue(field.value);
  const isRequired = hasRequiredValidation(fieldProps.validations);
  const options = useMemo(() => fieldProps.options || [], [fieldProps.options]);
  const disabled = fieldProps.disabled ?? false;

  const items = useMemo(() => options.map(option => {
    const label = String(option.text || option.value);
    const optionValue = normalizeValue(option.value);
    if (isPrinting) {
      return (<CheckIcon key={optionValue} isChecked={value === optionValue} label={label} />)
    }
    return (
      <Radio key={optionValue} value={optionValue} onBlur={field.onBlur}>{label}</Radio>
    );
  }), [field.onBlur, isPrinting, options, value]);

  const className = classnames(css.formPart);

  //const { value: _, onChange: __, ...other } = field;

  const handleChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    // The OPTION/INPUT field value is always a string.
    // In order to preserve the same type as the option, we'll locate the relevant option and retrieve the actual value form there.
    // See which option was seleced
    const option = options.find(o => normalizeValue(o.value) === value);
    console.assert(option, `This should not happen! Can't find matching option for: ${value} in collection.`, options);
    if (option) {
      form.setFieldValue(field.name, option.value);
      fieldChangedSignal.dispatch(field.name, option.value, field.value);
    }
  }, [field.name, field.value, fieldChangedSignal, form, options]);


  const hotizontalLayout  = fieldProps.orientation !== Orientation.Vertical;
  const control = isPrinting
    ? <div style={{display: 'flex', flexDirection: hotizontalLayout ? 'row' : 'column'}}>{items}</div>
    : <RadioGroup
        disabled={!isEditing || disabled}
        name={field.name}
        onChange={handleChange}
        selectedValue={value}
        inline={hotizontalLayout}
        {...fieldPropOverrides[field.name]}
      >
        {items}
      </RadioGroup>;

  return (
    <FormGroup
      label={fieldProps.label}
      labelInfo={<RequiredIndicator show={isRequired} isPrinting={isPrinting} />}
      intent={intent}
      helperText={isEditing ? (error || <>&nbsp;</>) : undefined}
      style={fieldProps.style}
      className={className}
    >
      {control}
    </FormGroup>)}


const RadioGroupField = (props : IRadioFieldProps) => {
  console.assert(props && props.binding);
  const { isEditing } = useFormContext();
  const validate = isEditing ? validationMethodFactory(props) : undefined;
  return <FastField name={props.binding} fieldProps={props} component={BoundRadioGroupField} validate={validate} />;
}


export default RadioGroupField;






